import "./style.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import { admissionsOfficeHeaderTabsConst } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAdmissionFormContext,
  setAdmissionFormContext,
  setTabsAdmissionsOffice,
} from "@/src/store/slices/AdmissionOffice/admissionOfficeDocSlice";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SelectDefault from "../../SelectDefault/SelectDefault";
import BtnDefault from "../../BtnDefault/BtnDefault";
import Refresh from "@/src/svg/header/Refresh";
import DatabaseSvg from "@/src/svg/admissionOfficePage/DatabaseSvg";
import VerticalLine from "../../VerticalLine/VerticalLine";
import CustomFullNamePopUp from "../../CustomUtils/CustomFullNamePopUp/CustomFullNamePopUp";
import { reloadPageAfterNewDocumentConfirm } from "@/src/utils/admissionOffice/confirmNewDocumentReload";
import { fetchApplicantUserInfo } from "@/src/utils/applicants/fetchApplicantUserInfo";
import { RootState } from "@/src/store";
import { IRowData } from "@/src/types/applicationOnline";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { usePushNotifications } from "../../CustomUtils/PushNotifications/PushNotificationsProvider";
import { tasksPageRequests } from "@/src/api/tasksPageRequests";
import { parseTaskApplicantId, parseTaskApplicantInfo } from "@/src/utils/tasks/parseTaskApplicantInfo";
import type { AdmissionOfficeCountry } from "@/src/utils/admissionOffice/resolveAdmissionOfficeRouteFromTask";

type AdmissionsLang = "en" | "fr" | "de" | "uzb";

const parsePositiveId = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const HeaderAdmissionsOffice = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notify } = usePushNotifications();
  const { taskId, applicantId } = useSelector((state: RootState) => state.admissionOfficeDoc);
  const hasFullCountryAccess = useSelector((state: RootState) => state.accessControl.hasFullCountryAccess);
  const allowedAdmissionCountries = useSelector(
    (state: RootState) => state.accessControl.allowedAdmissionCountries,
  );
  const [currentTab, setCurrentTab] = useState<string>("");
  const [currentSelect, setCurrentSelect] = useState<string>("");
  const [domTaskId, setDomTaskId] = useState("");
  const [domStudentId, setDomStudentId] = useState("");
  const [isApPopUpFullName, setIsApPopUpFullName] = useState(false);
  const [currentDataPopUp, setCurrentDataPopUp] = useState<IRowData["userInfo"] | undefined>();
  const [currentStudentId, setCurrentStudentId] = useState<number | undefined>();
  const [currentApplyInfoId, setCurrentApplyInfoId] = useState<number | undefined>();

  const visibleTabs = useMemo(() => {
    if (hasFullCountryAccess) return admissionsOfficeHeaderTabsConst;
    return allowedAdmissionCountries
      .map((country) => admissionsOfficeHeaderTabsConst.find((tab) => tab.id === country))
      .filter((tab): tab is (typeof admissionsOfficeHeaderTabsConst)[number] => Boolean(tab));
  }, [allowedAdmissionCountries, hasFullCountryAccess]);

  const effectiveTaskId = domTaskId || taskId;
  const effectiveApplicantId = parsePositiveId(domStudentId) ?? applicantId;
  const isUserInfoDisabled = !effectiveTaskId.trim() && effectiveApplicantId == null;

  const readTaskIdFromDom = useCallback(() => {
    const taskInput = document.querySelector("input#task_id") as HTMLInputElement | null;
    const nextTaskId = taskInput?.value.trim() ?? "";
    setDomTaskId(nextTaskId);
    if (nextTaskId) {
      dispatch(setAdmissionFormContext({ taskId: nextTaskId }));
    }
    return nextTaskId;
  }, [dispatch]);

  const readStudentIdFromDom = useCallback(() => {
    const studentInput = document.querySelector("input#student_id") as HTMLInputElement | null;
    const nextStudentId = studentInput?.value.trim() ?? "";
    setDomStudentId(nextStudentId);
    const parsedApplicantId = parsePositiveId(nextStudentId);
    if (parsedApplicantId != null) {
      dispatch(setAdmissionFormContext({ applicantId: parsedApplicantId }));
    }
    return nextStudentId;
  }, [dispatch]);

  const readIdsFromDom = useCallback(() => {
    readTaskIdFromDom();
    readStudentIdFromDom();
  }, [readStudentIdFromDom, readTaskIdFromDom]);

  const parseAdmissionsOfficeUrl = (pathname: string) => {
    const parts = pathname.split("/").filter(Boolean);

    if (parts[0] !== "admissions-office") return null;

    let lang: AdmissionsLang = "en";
    let country: string | undefined;
    let slug: string | undefined;

    if (parts[1] === "fr" || parts[1] === "de" || parts[1] === "uzb") {
      lang = parts[1];
      country = parts[2];
      slug = parts[3];
    } else {
      country = parts[1];
      slug = parts[2];
    }

    if (!country || !slug) return null;

    const tabObj = admissionsOfficeHeaderTabsConst.find((t) => t.id === country);
    if (!tabObj) return null;

    const option = tabObj.mass.find((m) => m.slug === slug && (m.lang ?? "en") === lang);
    if (!option) return null;

    return {
      tabId: tabObj.id,
      selectName: option.name,
      lang,
    };
  };

  useEffect(() => {
    const parsed = parseAdmissionsOfficeUrl(location.pathname);
    if (!parsed) return;

    const isAllowed =
      hasFullCountryAccess || allowedAdmissionCountries.includes(parsed.tabId as AdmissionOfficeCountry);

    if (!isAllowed) {
      const fallbackTab = visibleTabs[0];
      if (!fallbackTab?.mass?.[0]) return;
      const firstOption = fallbackTab.mass[0];
      const lang = (firstOption.lang ?? "en") as AdmissionsLang;
      const path =
        lang === "en"
          ? `/admissions-office/${fallbackTab.id}/${firstOption.slug}`
          : `/admissions-office/${lang}/${fallbackTab.id}/${firstOption.slug}`;
      navigate(path, { replace: true });
      return;
    }

    setCurrentTab(parsed.tabId);
    setCurrentSelect(parsed.selectName);
    dispatch(setTabsAdmissionsOffice(parsed.tabId));
  }, [
    allowedAdmissionCountries,
    dispatch,
    hasFullCountryAccess,
    location.pathname,
    navigate,
    visibleTabs,
  ]);

  useEffect(() => {
    dispatch(clearAdmissionFormContext());
    setDomTaskId("");
    setDomStudentId("");

    readIdsFromDom();
    const syncTimeouts = [100, 400, 1000, 2000].map((delay) => window.setTimeout(readIdsFromDom, delay));

    const onInput = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;

      if (target.id === "task_id") {
        const nextTaskId = target.value.trim();
        setDomTaskId(nextTaskId);
        dispatch(setAdmissionFormContext({ taskId: nextTaskId }));
      }

      if (target.id === "student_id") {
        const nextStudentId = target.value.trim();
        setDomStudentId(nextStudentId);
        dispatch(setAdmissionFormContext({ applicantId: parsePositiveId(nextStudentId) }));
      }
    };

    document.addEventListener("input", onInput);

    return () => {
      syncTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      document.removeEventListener("input", onInput);
    };
  }, [location.pathname, dispatch, readIdsFromDom]);

  const handleEventTab = (id: string) => {
    if (!hasFullCountryAccess && !allowedAdmissionCountries.includes(id as AdmissionOfficeCountry)) {
      return;
    }
    setCurrentTab(id);
    const tabObj = admissionsOfficeHeaderTabsConst.find((tab) => tab.id === id);
    if (tabObj && tabObj.mass.length > 0) {
      const firstOption = tabObj.mass[0];
      setCurrentSelect(firstOption.name);
      dispatch(setTabsAdmissionsOffice(id));
      navigateToPage(id, firstOption.name);
    } else {
      dispatch(setTabsAdmissionsOffice(id));
      navigateToPage(currentTab, currentSelect);
    }
  };

  const handleChangeEvent = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setCurrentSelect(value);
    navigateToPage(currentTab, value);
  };

  const navigateToPage = (tab: string, select: string) => {
    const tabObj = admissionsOfficeHeaderTabsConst.find((t) => t.id === tab);
    const option = tabObj?.mass.find((o) => o.name === select);
    const slug = option?.slug || "admission";
    const lang = (option?.lang ?? "en") as AdmissionsLang;
    const path = lang === "en" ? `/admissions-office/${tab}/${slug}` : `/admissions-office/${lang}/${tab}/${slug}`;
    navigate(path);
  };

  const refreshCurrentStudentUserInfo = useCallback(async () => {
    if (currentStudentId == null) return undefined;
    try {
      const userInfo = await fetchApplicantUserInfo(currentStudentId);
      if (userInfo) setCurrentDataPopUp(userInfo);
      return userInfo;
    } catch (error) {
      console.log("applicant request error:", error);
      return undefined;
    }
  }, [currentStudentId]);

  const openUserInfoByApplicantId = async (resolvedApplicantId: number, resolvedTaskRowId?: number | null) => {
    dispatch(
      setAdmissionFormContext({
        applicantId: resolvedApplicantId,
      }),
    );
    setCurrentStudentId(resolvedApplicantId);
    setCurrentApplyInfoId(resolvedTaskRowId ?? undefined);

    const userInfo = await fetchApplicantUserInfo(resolvedApplicantId);
    if (!userInfo) {
      notify({
        title: "Cannot open User Info",
        message: "Failed to load applicant information.",
        type: "error",
      });
      return false;
    }

    setCurrentDataPopUp(userInfo);
    setIsApPopUpFullName(true);
    return true;
  };

  const handleUserInfoClick = async () => {
    const normalizedTaskId = readTaskIdFromDom() || effectiveTaskId.trim();
    const normalizedStudentId = readStudentIdFromDom();
    const applicantIdFromStudent = parsePositiveId(normalizedStudentId) ?? effectiveApplicantId;

    dispatch(setIsPreloyder(true));
    try {
      if (normalizedTaskId) {
        const task = await tasksPageRequests.getById(normalizedTaskId);
        if (!task) {
          notify({
            title: "Cannot open User Info",
            message: "Task was not found for this Task Id.",
            type: "error",
          });
          return;
        }

        const applicantInfo = parseTaskApplicantInfo(task.applicant_info);
        const resolvedApplicantId = parsePositiveId(parseTaskApplicantId(applicantInfo, task.applicant_id));

        if (resolvedApplicantId == null) {
          notify({
            title: "Cannot open User Info",
            message: "Applicant ID is missing for this Task Id.",
            type: "error",
          });
          return;
        }

        const resolvedTaskRowId = parsePositiveId(task.id) ?? parsePositiveId(normalizedTaskId);
        dispatch(
          setAdmissionFormContext({
            taskId: normalizedTaskId,
            applicantId: resolvedApplicantId,
          }),
        );
        await openUserInfoByApplicantId(resolvedApplicantId, resolvedTaskRowId);
        return;
      }

      if (applicantIdFromStudent == null) return;

      await openUserInfoByApplicantId(applicantIdFromStudent);
    } catch (error) {
      console.log("applicant request error:", error);
      notify({
        title: "Cannot open User Info",
        message: "Failed to load applicant information.",
        type: "error",
      });
    } finally {
      dispatch(setIsPreloyder(false));
    }
  };

  return (
    <>
      <div className="header-admissions-office">
        <div className="header-bottom-tabs">
          {visibleTabs.map((tab) => (
            <div
              className={`header-bottom-tab ${tab.id === currentTab ? "active" : ""}`}
              key={tab.id}
              onClick={() => handleEventTab(tab.id)}
            >
              <BtnDefault>
                <p>{tab.title}</p>
              </BtnDefault>
            </div>
          ))}
        </div>
        <VerticalLine />
        {location.pathname !== "/documents-qr-code" && (
          <>
            <div className="header-bottom-select">
              <SelectDefault onChange={handleChangeEvent} value={currentSelect}>
                {admissionsOfficeHeaderTabsConst
                  .find((item) => item.id === currentTab)
                  ?.mass.map((option, index) => (
                    <option key={index} value={option.name}>
                      {option.name}
                    </option>
                  ))}
              </SelectDefault>
            </div>
            <VerticalLine />
          </>
        )}
        <Link to={"/documents-qr-code"}>
          <BtnDefault reverse={true}>
            <p>Documents QR Code</p>
            <DatabaseSvg />
          </BtnDefault>
        </Link>
        <div className="header-bottom-buttons">
          <BtnDefault disabled={isUserInfoDisabled} onClick={handleUserInfoClick}>
            <p>User Info</p>
          </BtnDefault>
          <BtnDefault onClick={reloadPageAfterNewDocumentConfirm}>
            <p>New document</p>
            <Refresh />
          </BtnDefault>
          <VerticalLine />
          <Link to={"/admissions-office-database"}>
            <BtnDefault reverse={true}>
              <p>Database</p>
              <DatabaseSvg />
            </BtnDefault>
          </Link>
        </div>
      </div>

      <CustomFullNamePopUp
        isApPopUpFullName={isApPopUpFullName}
        setIsApPopUpFullName={setIsApPopUpFullName}
        currentDataPopUp={currentDataPopUp}
        currentStudentId={currentStudentId}
        currentApplyInfoId={currentApplyInfoId}
        tableContext="tasks"
        onRefreshCurrentStudentUserInfo={refreshCurrentStudentUserInfo}
      />
    </>
  );
};

export default HeaderAdmissionsOffice;
